import { useApolloClient } from '@apollo/react-hooks';
import { IDropdownOption } from '@fluentui/react';
import TextInput from 'components/controls/TextInput';
import ToggleGroup from 'components/controls/ToggleGroup';
import { ConditionalRender } from 'components/layout/ConditionalRender';
import { anonimityOptions } from 'components/ObservationReporter/constants/observation-reporter.constants';
import { GetObservationReporterTypeLookups } from 'components/ObservationReporter/queries/observation-reporter.queries';
import {
   IObservationReporterLookupData,
   IObservationReporterType
} from 'components/ObservationReporter/queries/observation-reporter.queries.types';
import { useForm } from 'modules/forms';
import { useEffect, useState } from 'react';
import { ObservationReporterTypeEnum } from 'shared/enums/ObservationReporterType.enum';
import { StyledControlContainer } from 'shared/styled/control.styled';
import { StyledPanelTitle } from 'shared/styled/panel.stayled';
import { OpportunityEntity } from 'shared/types/domain/OpportunityEntity';
import { mapToDropdownOptions } from 'shared/utils/mapping.utils';
import { useSecurity } from 'modules/security';
import { isNullOrUndefined } from 'shared/utils/null.utils';

interface NearMissReporterProps {
   nearMissReadOnly: boolean;
   reporterReadOnly: boolean;
}

const OpportunityReporter = ({ reporterReadOnly, nearMissReadOnly: isReadOnly }: NearMissReporterProps) => {
   const { formData, updateFields, formErrors } = useForm<OpportunityEntity>();
   const [loadingReporterTypes, setLoadingReporterTypes] = useState<boolean>(true);
   const [reporterTypes, setReporterTypes] = useState<IObservationReporterType[]>();

   const client = useApolloClient();

   const { user } = useSecurity();

   useEffect(() => {
      loadReporterTypes();
   }, []);

   const loadReporterTypes = async () => {
      setLoadingReporterTypes(true);

      try {
         let result = await client.query<IObservationReporterLookupData>({
            query: GetObservationReporterTypeLookups,
            variables: {
               empId: parseInt(user?.employeeId!)
            },
            fetchPolicy: 'network-only'
         });

         if (result?.data?.nearMissReporters != null) {
            setReporterTypes(result.data.nearMissReporters);
            if (isNullOrUndefined(formData?.nearMissId)) {
               updateFields({
                  reporterContactName: {
                     runValidation: true,
                     value: result.data.observationReporterDetails?.displayName!
                  }
               });
               updateFields({
                  reporterPhoneNumber: {
                     runValidation: true,
                     value:
                        result.data.observationReporterDetails?.businessPhones!.length > 0
                           ? result.data.observationReporterDetails.businessPhones[0]
                           : null
                  }
               });
               updateFields({
                  reporterEmail: { runValidation: true, value: result.data.observationReporterDetails?.email! }
               });
            }
         }
      } finally {
         setLoadingReporterTypes(false);
      }
   };

   const getAnonymousType = (selectedOption: boolean | null) => {
      if (selectedOption === true) {
         return 1;
      } else if (selectedOption === false) {
         return 0;
      }
      return 2;
   };

   return (
      <>
         <StyledPanelTitle>Who is reporting the Opportunity?</StyledPanelTitle>
         <StyledControlContainer>
            <ToggleGroup
               label={'Are You a…'}
               options={mapToDropdownOptions<IObservationReporterType, IDropdownOption>(
                  reporterTypes,
                  type => type.name,
                  type => type.description
               )}
               selectedKey={formData?.reporterType}
               onChange={v =>
                  updateFields({
                     reporterType: { runValidation: false, value: v as ObservationReporterTypeEnum }
                  })
               }
               isLoading={loadingReporterTypes}
               required
               disabled={isReadOnly}
            />
         </StyledControlContainer>

         <StyledControlContainer>
            <ToggleGroup
               label={"Do You Want To Report This Anonymously?"}
               options={[
                  { key: 1, text: 'Yes', data: "You can report anonymously. However if you enter your contact details we will be able to update you" },
                  { key: 0, text: 'No', data: "You can report anonymously. However if you enter your contact details we will be able to update you" }
               ]}
               selectedKey={getAnonymousType(formData?.anonymousEntry)}
               onChange={v =>
                  updateFields({
                     anonymousEntry: { runValidation: false, value: !!v }
                  })
               }
               required
               disabled={isReadOnly}
            />
         </StyledControlContainer>

         <ConditionalRender condition={!getAnonymousType(formData?.anonymousEntry)}>
            <StyledControlContainer>
               <StyledPanelTitle>Your Contact Details</StyledPanelTitle>
               <TextInput
                  label={'Your Name'}
                  value={formData?.reporterContactName}
                  errorMessage={formErrors?.reporterContactName}
                  onChange={(e, v) =>{
                     updateFields({
                        reporterContactName: { runValidation: true, value: v }
                     })}
                  }
                  required
                  disabled={reporterReadOnly}
               />
               <TextInput
                  label={'Contact Phone'}
                  value={formData?.reporterPhoneNumber}
                  errorMessage={formErrors?.reporterPhoneNumber}
                  onChange={(e, v) =>
                     updateFields({
                        reporterPhoneNumber: { runValidation: true, value: v }
                     })
                  }
                  required
                  disabled={reporterReadOnly}
               />
               <TextInput
                  label={'Contact Email'}
                  value={formData?.reporterEmail}
                  errorMessage={formErrors?.reporterEmail}
                  onChange={(e, v) =>
                     updateFields({
                        reporterEmail: { runValidation: true, value: v }
                     })
                  }
                  required
                  disabled={reporterReadOnly}
               />
            </StyledControlContainer>
         </ConditionalRender>
      </>
   );
};

export default OpportunityReporter;
